import type { RouterNavigateOptions } from '@remix-run/router';
import type { ProjectDetail } from '@/stores/project/types';

import { resetAllProjectStores } from '@/helpers/storeReset';
import { collect } from '@/modules/Collect';
import { navigate } from '@/modules/CustomRouter/handler';
import { canUseAnonymousProjectMode } from '@/modules/CustomRouter/helper';
import { applyProjectDetail } from '@/pages/Projects/helper';
import {
  createAnonymousProject,
  createProject,
  getProjectDetail,
  getProjects,
  PROJECT_PAGE_SIZE,
} from '@/services/project';
import { useChatStore } from '@/stores/chat';
import { ProjectStatus, Stage, useProjectStore } from '@/stores/project';
import { useUserStore } from '@/stores/user';
import { setAnonymousProjectId } from './storage';

export { ANONYMOUS_PROJECT_LIMIT } from './anonymousLimit';

export function buildProjectPath(projectId: string, searchParams?: URLSearchParams): string {
  const basePath = `/workspace/projects/${projectId}`;
  return searchParams ? `${basePath}?${searchParams.toString()}` : basePath;
}

interface SwitchProjectOptions {
  /** 是否进入预览模式 */
  enterPreview?: boolean;
}

interface CreateProjectOptions {
  beforeNavigate?: (projectId: string) => Promise<void> | void;
  skipAccessPreflight?: boolean;
  source?: string;
}

interface CreateProjectRecordOptions {
  source?: string;
}

interface CreateProjectRecordResult {
  projectId: string;
  isLogin: boolean;
}

function buildNewlyCreatedProjectRouteState(projectId: string, options?: CreateProjectOptions) {
  if (!options?.skipAccessPreflight) {
    return undefined;
  }

  return {
    newlyCreatedProjectId: projectId,
  };
}

export async function prepareProjectEntry(projectId: string, isLogin: boolean) {
  useChatStore.getState().setIsMessageLoading(true);

  try {
    const { data } = await getProjectDetail(projectId);
    applyProjectDetail(data, isLogin);
  } catch (error) {
    console.warn('preload project detail failed:', error);
  }
}

/**
 * 切换到指定项目
 * 统一处理项目切换逻辑，包括状态重置、设置新 ID、导航
 */
export function switchProject(projectId: string, options?: SwitchProjectOptions) {
  if (!projectId) {
    return;
  }

  // 1. 重置所有项目相关状态
  resetAllProjectStores();

  // 2. 设置新的 projectId 和初始 stage
  const { setProjectId, setStage } = useProjectStore.getState();
  setProjectId(projectId);
  setStage(Stage.Input);

  if (options?.enterPreview) {
    useChatStore.getState().setIsChatPanelVisible(false);
    useProjectStore.getState().setStatus(ProjectStatus.GENERATED);
  }

  // 3. 导航到新项目
  navigate(buildProjectPath(projectId));
}

export async function createProjectRecord(
  projectName: string,
  options?: CreateProjectRecordOptions,
): Promise<CreateProjectRecordResult> {
  const { isLogin } = useUserStore.getState();
  const shouldCreateAnonymousProject = canUseAnonymousProjectMode(isLogin);
  const startTime = Date.now();
  const source = options?.source || 'direct';

  try {
    const { data } = shouldCreateAnonymousProject
      ? await createAnonymousProject(projectName)
      : await createProject(projectName);

    if (shouldCreateAnonymousProject) {
      setAnonymousProjectId(data.projectId);
    }

    collect('create_project', {
      status: 'success',
      duration: Date.now() - startTime,
      project_id: data.projectId,
      source,
    });

    return {
      projectId: data.projectId,
      isLogin,
    };
  } catch (error) {
    collect('create_project', {
      status: 'fail',
      duration: Date.now() - startTime,
      project_id: '',
      source,
    });

    throw error;
  }
}

/**
 * 创建新项目并跳转到项目详情页
 * 未登录时走匿名创建接口，登录后由 claim-all 归属
 */
export async function createProjectAndNavigate(
  projectName: string,
  searchParams?: URLSearchParams,
  navigateOptions?: RouterNavigateOptions,
  options?: CreateProjectOptions,
) {
  const { projectId, isLogin } = await createProjectRecord(projectName, {
    source: options?.source,
  });

  // 2. 重置所有项目相关状态
  resetAllProjectStores();

  // 3. 设置项目 ID 和初始 stage
  const { setProjectId, setStage, setIsNewlyCreated } = useProjectStore.getState();
  setProjectId(projectId);
  setStage(Stage.Input);

  // 4. 标记为新创建的项目，避免重复启动沙盒
  setIsNewlyCreated(true);

  await Promise.all([
    prepareProjectEntry(projectId, isLogin),
    options?.beforeNavigate?.(projectId),
  ]);

  // 5. 跳转到项目详情页
  const url = buildProjectPath(projectId, searchParams);
  navigate(url, {
    ...navigateOptions,
    state: buildNewlyCreatedProjectRouteState(projectId, options),
  });

  return { projectId };
}

/**
 * 获取用户所有项目
 */
export async function getAllUserProjects(): Promise<ProjectDetail[]> {
  const allProjects: ProjectDetail[] = [];
  let page = 1;
  let hasNextPage = true;

  while (hasNextPage) {
    const res = await getProjects(page, PROJECT_PAGE_SIZE);
    allProjects.push(...(res.data.list || []));
    hasNextPage = res.data.pagination?.hasNextPage ?? false;
    page++;
  }

  return allProjects;
}
